import { AppModule } from 'app/module';
import { userNotifications } from 'app/userNotifications';
import { NotificationBar } from 'module/notificationBar/main';
import { HeaderSmall } from 'module/headerSmall/main';
import { Footer } from 'module/footer/main';
import { LightBox } from 'module/lightBox/main';
import {
    sendServerSideGa4Events,
} from 'util/googleAnalytics';
import { LoginModal } from 'module/loginModal/main';
import { SmallFooter } from 'module/smallFooter/main';
import {
    ENDPOINT_USER_LOGIN,
    ENDPOINT_USER_REGISTER,
} from 'app/endpoints';
import { REGEX_JOBSEARCH_CONTROLLER } from 'app/regularExpressions';
import client from '@karriereat/mf-header-client';
import { showSnackbar } from 'util/snackbar';
import './styles.scss';

let headerClientReady = false;

export class MasterLayout extends AppModule {
    domBindings() {
        return {
            html: 'html',
            body: 'body',
            master: '.l-master',
            notificationBar: '.m-notificationBar',
            footer: '.m-footer',
            smallFooter: '.l-master__footer .m-smallFooter',
            lightBox: '.m-lightBox',
            content: '.l-master__content',
            loginModal: '.m-loginModal',
            headerContainer: '.l-master__headerContainer',
            headerSmallContainer: '.l-master__headerSmallContainer',
        };
    }

    domEvents() {
        window.addEventListener('mf-header:click-loginButton', () => this.onLoginButtonClick());
        window.addEventListener('mf-header:click-registerLink', () => this.onRegisterLinkClick());
        window.addEventListener('mf-header:click-nav-item', (e) => this.onNavItemClick(e));
    }

    subs() {
        this.notificationBar = new NotificationBar(this.dom.notificationBar);
        if (this.dom.footer) {
            this.footer = new Footer(this.dom.footer);
        }
        this.lightBox = new LightBox(this.dom.lightBox);
        if (this.dom.loginModal) {
            this.loginModal = new LoginModal(this.dom.loginModal);
        }
        if (this.dom.headerSmallContainer) {
            this.headerSmallContainer = new HeaderSmall(this.dom.headerSmallContainer);
        }
        if (this.dom.smallFooter) {
            this.smallFooter = new SmallFooter(this.dom.smallFooter);
        }
    }

    subEvents() {
        this.lightBox.events.on('showContent', () => this.showContent());
        this.lightBox.events.on('hideContent', () => this.hideContent());
        this.lightBox.events.on('scrollToSection', () => {
            this.events.emit('scrollToSection');
        });
        this.lightBox.events.on('pushState', () => {
            this.events.emit('pushState');
        });
        if (this.loginModal) {
            this.loginModal.events.on('removeModalStyle', () => {
                this.removeModalStyle();
            });
            this.loginModal.events.on('showSnackbar', (e) => {
                showSnackbar({
                    text: e.arguments[0],
                });
            });
        }
        userNotifications.events.on('update', () => this.updateUserNotifications());
    }

    hideContent() {
        this.props.scrollPosition = window.pageYOffset;
        this.dom.headerContainer?.classList.add('l-master__headerContainer--hide');
        this.dom.headerSmallContainer?.classList.add('l-master__headerSmallContainer--hide');
        this.dom.content.classList.add('l-master__content--hide');
        this.dom.footer.classList.add('m-footer--hide');
    }

    showContent() {
        this.dom.headerContainer?.classList.remove('l-master__headerContainer--hide');
        this.dom.headerSmallContainer?.classList.remove('l-master__headerSmallContainer--hide');
        this.dom.content.classList.remove('l-master__content--hide');
        this.dom.footer.classList.remove('m-footer--hide');
        if (this.props.scrollPosition) {
            window.scrollTo(0, this.props.scrollPosition);
        }
    }

    hideFooter() {
        if (this.dom.smallFooter && !this.dom.smallFooter.classList.contains('m-smallFooter--hide')) {
            this.dom.smallFooter.classList.add('m-smallFooter--hide');
        }
    }

    showFooter() {
        if (this.dom.smallFooter && this.dom.smallFooter.classList.contains('m-smallFooter--hide')) {
            this.dom.smallFooter.classList.remove('m-smallFooter--hide');
        }
    }

    ready() {
        client.onReady(this.headerClientReady);
        sendServerSideGa4Events();
    }

    headerClientReady() {
        headerClientReady = true;
        client.updateNotifications(userNotifications.notifications);
    }

    updateUserNotifications() {
        if (headerClientReady) {
            client.updateNotifications(userNotifications.notifications);
        }
    }

    onLoginButtonClick() {
        this.openLoginModal(`${ENDPOINT_USER_LOGIN}?redirectUrl=${encodeURIComponent(window.location.href)}`, true);
    }

    onNavItemClick(e) {
        this.openLoginModal(`${ENDPOINT_USER_LOGIN}?redirectUrl=${e.detail.redirectUrl}`, true, true);
    }

    onRegisterLinkClick() {
        this.openLoginModal(`${ENDPOINT_USER_REGISTER}`, false, false, true);
    }

    addModalStyle(captcha = false) {
        const scrollPosition = window.pageYOffset;

        if (this.dom.html.style.height !== '100vh') {
            this.dom.html.style.height = '100vh';
        }

        if (this.dom.body.style.overflow !== 'hidden') {
            this.dom.body.style.height = '100%';
            this.dom.body.style.width = '100%';
            this.dom.body.style.overflow = 'hidden';
            this.dom.body.style.position = 'fixed';

            if (captcha) {
                this.dom.body.setAttribute('data-position', `-${scrollPosition}px`);
            } else {
                this.dom.body.style.top = `-${scrollPosition}px`;
            }
        }
    }

    removeModalStyle(captcha = false) {
        let scrollPosition = 0;

        if (captcha) {
            scrollPosition = this.dom.body.getAttribute('data-position');
        } else {
            scrollPosition = this.dom.body.style.top;
        }

        scrollPosition = scrollPosition !== 0 ? parseInt(scrollPosition.substr(1, scrollPosition.length), 10) : 0;

        if (this.dom.html.style.height === '100vh') {
            this.dom.html.style.height = '';
        }

        if (this.dom.body.style.overflow !== 'visible') {
            this.dom.body.style.height = 'auto';
            this.dom.body.style.width = 'auto';
            this.dom.body.style.overflow = 'visible';
            this.dom.body.style.position = 'static';
            if (scrollPosition !== 0) {
                window.scrollTo(0, scrollPosition);
            }
        }
    }

    openLoginModal(
        src = false,
        hasParams = false,
        commingFromJobSearchPage = false,
        register = false,
    ) {
        const isJobSearch = new RegExp(REGEX_JOBSEARCH_CONTROLLER).test(window.location.pathname);

        if (isJobSearch && !commingFromJobSearchPage && src) {
            this.events.emit('getUserJobSearchRequest', register);
        } else if (this.loginModal) {
            this.loginModal.toggle(src, hasParams);
        }
    }
}
